import { Button, Flex, Input, Radio, Space, Spin, Tooltip } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Modal } from "ui/Modal"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import { useState, useCallback } from "react"
import styles from "./index.module.css"
import CustomDrawer from "ui/Drawer"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { useSelector, useDispatch } from "react-redux"
import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons"
import { createRefund, getClientBookingDetails } from "store/bookings"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"
import { roundToCents } from "utils/bookingCalculations"
import { PAYMENTS } from "./constants"
import RefundBreakdownList from "./RefundBreakdownList"
import { BOOKING_SOURCE } from "pagesV2/pro/Bookings/constants"

const RefundModal = ({ onOpen = false, onClose, bookingUUID }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)
  const { loading: refundLoading } = useSelector((state) => state.bookings.refund)
  const { data, loading } = useSelector((state) => state.bookings.refundDetails)
  const {
    total_paid = 0,
    non_refundable_amount = 0,
    refundable_amount = 0,
    stripe_total,
    stripe_incomes,
    paypal_incomes,
    paypal_total,
    booking_source = "",
    is_within_48_hours = false
  } = data || {}

  const formattedTotalPaid = numberToCurrency(total_paid)
  const formattedNonRefundableDeposit = numberToCurrency(non_refundable_amount)
  const formattedRefundableAmount = numberToCurrency(refundable_amount)

  // Determine if we should show non-refundable deposit label with info
  const isMarketplaceBooking = booking_source === BOOKING_SOURCE.marketplace
  const showNonRefundableDeposit = isMarketplaceBooking && is_within_48_hours
  const depositLabel = showNonRefundableDeposit ? t("client.payment.nonRefundableDeposit") : t("client.payment.deposit")

  const [paymentType, setPaymentType] = useState(PAYMENTS.CASH)
  const [refundAmount, setRefundAmount] = useState("")
  const [refundError, setRefundError] = useState("")
  const dispatch = useDispatch()

  const validateRefundAmount = useCallback(
    (value, paymentType = PAYMENTS.CASH) => {
      if (value === "" || value === null) {
        return t("client.payment.refundAmountRequired")
      }
      if (Number.isNaN(value) || Number(value) <= 0) {
        return t("client.payment.refundAmountGreaterThanZero")
      }
      if (Number(value) > roundToCents(total_paid)) {
        return t("client.payment.refundAmountCannotExceed", { amount: numberToCurrency(total_paid) })
      }
      if (paymentType === PAYMENTS.STRIPE && Number(value) > roundToCents(stripe_total)) {
        return t("client.payment.refundAmountCannotExceedCard", { amount: numberToCurrency(stripe_total) })
      }
      if (paymentType === PAYMENTS.PAYPAL && Number(value) > roundToCents(paypal_total)) {
        return t("client.payment.refund_amount_cannot_exceed_paypal", { amount: numberToCurrency(paypal_total) })
      }
      return ""
    },
    [stripe_total, refundable_amount, t]
  )

  const handleRefundChange = (e) => {
    const value = e.target.value
    setRefundAmount(value)
    setRefundError(validateRefundAmount(value, paymentType))
  }

  const handlePaymentTypeChange = (e) => {
    const value = e.target?.value
    setPaymentType(value)
    setRefundError(validateRefundAmount(refundAmount, value))
  }

  const handleSubmit = async () => {
    const error = validateRefundAmount(refundAmount, paymentType)
    setRefundError(error)
    if (error) return
    try {
      const response = await dispatch(createRefund(bookingUUID, { amount: roundToCents(refundAmount), payment_type: paymentType }))
      toaster.success(response?.message)
      dispatch(getClientBookingDetails(bookingUUID))
    } catch (error) {
      console.error(error)
    } finally {
      onClose()
    }
  }

  const modalContent = (
    <Spin spinning={loading || refundLoading} indicator={<LoadingOutlined spin />} size="large">
      <Space vertical size={24} className="w-full" direction="vertical">
        <Space className={`w-full ${styles.amountContainer}`} size={spacings["4XL"]}>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>{t("client.payment.totalPaid")}</Text>
            <Text color={colors.textPrimary}>{formattedTotalPaid}</Text>
          </Flex>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Flex gap={spacings.XXS} align="center">
              <Text color={colors.textSecondaryDark}>{depositLabel}</Text>
              {showNonRefundableDeposit && (
                <Tooltip title={t("client.payment.nonRefundableDepositInfo")}>
                  <InfoCircleOutlined style={{ color: colors.textSecondaryDark, cursor: "pointer" }} />
                </Tooltip>
              )}
            </Flex>
            <Text color={colors.textPrimary}>{formattedNonRefundableDeposit}</Text>
          </Flex>
          <Flex gap={spacings.S} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>{t("client.payment.refundableAmount")}</Text>
            <Text color={colors.textPrimary}>{formattedRefundableAmount}</Text>
          </Flex>
        </Space>
        <Radio.Group value={paymentType} onChange={handlePaymentTypeChange}>
          <Space direction="horizontal" wrap>
            <Radio value={PAYMENTS.CASH}>{t("client.payment.inCash")}</Radio>
            <Radio value={PAYMENTS.STRIPE} disabled={stripe_incomes?.length === 0}>
              {t("client.payment.toStripe")}
            </Radio>
            <Radio value={PAYMENTS.PAYPAL} disabled={paypal_incomes?.length === 0}>
              {t("client.payment.toPaypal")}
            </Radio>
          </Space>
        </Radio.Group>
        {(paymentType === PAYMENTS.STRIPE || paymentType === PAYMENTS.PAYPAL) && <Text>{t("client.payment.refundCardNote")}</Text>}
        <Flex vertical gap={spacings.XXS}>
          <Text>{t("client.payment.refundAmountLabel")}</Text>
          <Input
            value={refundAmount}
            prefix="$"
            type="number"
            min={0}
            // max={formattedRefundableAmount}
            step="0.01"
            onChange={handleRefundChange}
            status={refundError ? "error" : ""}
            placeholder="Enter refund amount"
          />
          {refundError && <Text color={colors.error}>{refundError}</Text>}
        </Flex>
        {(paymentType === PAYMENTS.STRIPE || paymentType === PAYMENTS.PAYPAL) && (
          <RefundBreakdownList
            stripeIncomes={stripe_incomes}
            paypalIncomes={paypal_incomes}
            formattedRefundableAmount={formattedRefundableAmount}
            paymentType={paymentType}
          />
        )}
        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button type="primary" className="py-l px-xl" disabled={!!refundError || !refundAmount} onClick={handleSubmit}>
            {t("global.submit")}
          </Button>
        </Flex>
      </Space>
    </Spin>
  )

  return isMobile ? (
    <CustomDrawer open={onOpen} setOpen={onClose} title={t("client.payment.refund")}>
      {modalContent}
    </CustomDrawer>
  ) : (
    <Modal centered title={t("client.payment.refund")} open={onOpen} onOk={onClose} onCancel={onClose} width={800}>
      {modalContent}
    </Modal>
  )
}

RefundModal.propTypes = {
  onOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  bookingUUID: PropTypes.string.isRequired
}

export default RefundModal
